import React from 'react';
import {
  Box,
  TextField,
  Button,
  Autocomplete,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useLocationSelector, usePostalCodeSearch } from '../hooks/useAddressData';

export interface AddressSelectorProps {
  onAddressSelect: (address: any) => void; // 柔軟な型にして両方のページで使用可能にする
  selectedAddress?: any;
  compact?: boolean; // コンパクト表示（アコーディオンなし）
}

export const AddressSelector: React.FC<AddressSelectorProps> = ({
  onAddressSelect,
  selectedAddress,
  compact = false
}) => {
  const {
    prefectures,
    cities,
    selectedPrefecture,
    selectedCity,
    isLoading,
    setSelectedPrefectureCode,
    setSelectedCityCode,
    resetSelection,
    getLocationString
  } = useLocationSelector();

  const {
    searchByPostalCode,
    isSearching: isSearchingPostalCode,
    searchError: postalCodeError
  } = usePostalCodeSearch();

  const [searchZipcode, setSearchZipcode] = React.useState('');

  // 都道府県・市区町村での検索
  const handleLocationSearch = () => {
    if (selectedPrefecture && selectedCity) {
      // 両方の形式でデータを渡す（後方互換性のため）
      onAddressSelect({
        // 新しい形式（ProfilePage用）
        prefecture: selectedPrefecture.name,
        city: selectedCity.name,
        prefecture_code: selectedPrefecture.code,
        city_code: selectedCity.code,
        address: `${selectedPrefecture.name}${selectedCity.name}`,
        // 古い形式（CreateReviewPage用）
        prefectureCode: selectedPrefecture.code,
        cityCode: selectedCity.code
      });
    }
  };

  // 郵便番号での検索
  const handleZipcodeSearch = async () => {
    if (!searchZipcode.trim()) return;

    try {
      const result = await searchByPostalCode(searchZipcode);
      if (result) {
        // 郵便番号検索結果から都道府県コードと市区町村コードを推定
        const prefecture = prefectures.find(p => p.name === result.prefecture);
        const prefecture_code = prefecture?.code || '';
        
        // 両方の形式でデータを渡す（後方互換性のため）
        onAddressSelect({
          // 新しい形式（ProfilePage用）
          postal_code: searchZipcode,
          prefecture: result.prefecture,
          city: result.city,
          prefecture_code,
          city_code: '', // 郵便番号検索では市区町村コードは取得できない
          address: `${result.prefecture}${result.city}${result.address || ''}`,
          // 古い形式（CreateReviewPage用）
          prefectureCode: prefecture_code,
          cityCode: '' // 郵便番号検索では市区町村コードは取得できない
        });
      }
    } catch (error) {
      console.error('郵便番号検索エラー:', error);
    }
  };

  const LocationSelectionContent = () => (
    <Stack spacing={2}>
      {/* 郵便番号検索 */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          郵便番号で検索
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="郵便番号"
            placeholder="1000001"
            value={searchZipcode}
            onChange={(e) => setSearchZipcode(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            onClick={handleZipcodeSearch}
            disabled={isSearchingPostalCode}
            startIcon={isSearchingPostalCode ? <CircularProgress size={16} /> : <SearchIcon />}
          >
            検索
          </Button>
        </Box>
        {postalCodeError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {postalCodeError}
          </Typography>
        )}
      </Box>

      {/* 都道府県・市区町村検索 */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          都道府県・市区町村で検索
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            options={prefectures}
            getOptionLabel={(option) => option.name}
            value={selectedPrefecture}
            onChange={(_, value) => setSelectedPrefectureCode(value?.code || '')}
            disabled={isLoading}
            sx={{ minWidth: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="都道府県"
                size="small"
              />
            )}
          />

          <Autocomplete
            options={cities}
            getOptionLabel={(option) => option.name}
            value={selectedCity}
            onChange={(_, value) => setSelectedCityCode(value?.code || '')}
            disabled={!selectedPrefecture || isLoading}
            sx={{ minWidth: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="市区町村"
                size="small"
              />
            )}
          />

          <Button
            variant="contained"
            onClick={handleLocationSearch}
            disabled={!selectedPrefecture}
            startIcon={<SearchIcon />}
          >
            選択
          </Button>
        </Box>

        {/* 選択中の地域表示 */}
        {(getLocationString() || selectedAddress) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              選択中の地域:
            </Typography>
            <Chip
              label={selectedAddress ? `${selectedAddress.prefecture} ${selectedAddress.city}` : getLocationString()}
              color="primary"
              variant="outlined"
              icon={<LocationOnIcon />}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );

  if (compact) {
    return <LocationSelectionContent />;
  }

  return (
    <Stack spacing={2}>
      {/* 郵便番号検索 */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>郵便番号で検索</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="郵便番号"
              placeholder="1000001"
              value={searchZipcode}
              onChange={(e) => setSearchZipcode(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="contained"
              onClick={handleZipcodeSearch}
              disabled={isSearchingPostalCode}
              startIcon={isSearchingPostalCode ? <CircularProgress size={16} /> : <SearchIcon />}
            >
              検索
            </Button>
          </Box>
          {postalCodeError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {postalCodeError}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* 都道府県・市区町村検索 */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>都道府県・市区町村で検索</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Autocomplete
                options={prefectures}
                getOptionLabel={(option) => option.name}
                value={selectedPrefecture}
                onChange={(_, value) => setSelectedPrefectureCode(value?.code || '')}
                disabled={isLoading}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="都道府県"
                    size="small"
                  />
                )}
              />

              <Autocomplete
                options={cities}
                getOptionLabel={(option) => option.name}
                value={selectedCity}
                onChange={(_, value) => setSelectedCityCode(value?.code || '')}
                disabled={!selectedPrefecture || isLoading}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="市区町村"
                    size="small"
                  />
                )}
              />

              <Button
                variant="contained"
                onClick={handleLocationSearch}
                disabled={!selectedPrefecture}
                startIcon={<SearchIcon />}
              >
                選択
              </Button>
            </Box>

            {/* 選択中の地域表示 */}
            {(getLocationString() || selectedAddress) && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  選択中の地域:
                </Typography>
                <Chip
                  label={selectedAddress ? `${selectedAddress.prefecture} ${selectedAddress.city}` : getLocationString()}
                  color="primary"
                  variant="outlined"
                  icon={<LocationOnIcon />}
                />
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};
